import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatIDR } from '@/lib/format';
import { TrendingUp, Gamepad2, UtensilsCrossed, Activity, AlertTriangle } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const KPI_CARDS = [
  { key: 'total', label: 'Total Omset Hari Ini', icon: TrendingUp, accent: 'cyan', testid: 'kpi-total-omset' },
  { key: 'rental', label: 'Omset Rental PS', icon: Gamepad2, accent: 'violet', testid: 'kpi-rental-omset' },
  { key: 'food', label: 'Omset Makanan & Minuman', icon: UtensilsCrossed, accent: 'magenta', testid: 'kpi-food-omset' },
  { key: 'transactions', label: 'Transaksi Hari Ini', icon: Activity, accent: 'lime', testid: 'kpi-transactions', isCount: true },
];

const accentMap = {
  cyan: { card: 'neon-cyan-card', text: 'text-cyan-400', iconBg: 'bg-cyan-500/10 text-cyan-400' },
  magenta: { card: 'neon-magenta-card', text: 'text-fuchsia-400', iconBg: 'bg-fuchsia-500/10 text-fuchsia-400' },
  lime: { card: 'neon-lime-card', text: 'text-emerald-400', iconBg: 'bg-emerald-500/10 text-emerald-400' },
  violet: { card: 'neon-violet-card', text: 'text-violet-400', iconBg: 'bg-violet-500/10 text-violet-400' },
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [chart, setChart] = useState([]);

  const load = async () => {
    const [s, c] = await Promise.all([api.summary(), api.dailyChart(7)]);
    setSummary(s);
    setChart(c);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const kpiValue = (key) => {
    if (!summary) return key === 'transactions' ? 0 : 0;
    if (key === 'total') return summary.today.total;
    if (key === 'rental') return summary.today.rental;
    if (key === 'food') return summary.today.food;
    if (key === 'transactions') return summary.today.transactions;
    return 0;
  };

  const pieData = summary ? [
    { name: 'Rental PS', value: summary.all_time.rental, color: '#00F0FF' },
    { name: 'Makanan & Minuman', value: summary.all_time.food, color: '#FF007F' },
  ] : [];
  const hasPie = pieData.some(d => d.value > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Dashboard Operasional</h2>
        <p className="text-slate-400 text-sm mt-1">Ringkasan omset, aktivitas rental, dan status stok makanan hari ini.</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        {KPI_CARDS.map(({ key, label, icon: Icon, accent, testid, isCount }) => {
          const a = accentMap[accent];
          return (
            <div key={key} data-testid={testid} className={`rounded-xl bg-[#131B2E] p-5 ${a.card}`}>
              <div className="flex items-start justify-between">
                <p className="text-xs font-mono-tactical uppercase tracking-widest text-slate-400 leading-snug max-w-[70%]">{label}</p>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${a.iconBg}`}>
                  <Icon className="w-4 h-4" />
                </div>
              </div>
              <p className={`mt-4 font-mono-tactical text-2xl sm:text-3xl font-bold ${a.text}`}>
                {isCount ? kpiValue(key) : formatIDR(kpiValue(key))}
              </p>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-2 rounded-xl bg-[#131B2E] p-5 border border-slate-800">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-semibold">Tren Omset 7 Hari Terakhir</h3>
              <p className="text-xs text-slate-500 mt-0.5">Perbandingan pendapatan rental vs makanan</p>
            </div>
          </div>
          <div className="h-72" data-testid="chart-daily-omset">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gRental" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#00F0FF" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="gFood" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF007F" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#FF007F" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="date" stroke="#64748B" tick={{ fill: '#64748B', fontSize: 11 }} />
                <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: '#0E1424', border: '1px solid #1E293B', borderRadius: 8, color: '#F8FAFC' }}
                  formatter={(v) => formatIDR(v)}
                />
                <Area type="monotone" dataKey="rental" stroke="#00F0FF" strokeWidth={2} fill="url(#gRental)" name="Rental PS" />
                <Area type="monotone" dataKey="food" stroke="#FF007F" strokeWidth={2} fill="url(#gFood)" name="Makanan" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl bg-[#131B2E] p-5 border border-slate-800">
          <h3 className="font-display text-lg font-semibold">Porsi Pendapatan</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-4">Total sepanjang waktu</p>
          <div className="h-72" data-testid="chart-pie-breakdown">
            {hasPie ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={4}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} stroke="#131B2E" strokeWidth={3} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0E1424', border: '1px solid #1E293B', borderRadius: 8 }} formatter={(v) => formatIDR(v)} />
                  <Legend wrapperStyle={{ color: '#94A3B8', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">
                Belum ada data omset
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-xl bg-[#131B2E] p-5 border border-slate-800 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
            <Gamepad2 className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-mono-tactical">Unit Aktif</p>
            <p className="font-mono-tactical text-2xl font-bold text-emerald-400" data-testid="stat-active-rentals">
              {summary?.active_rentals ?? 0} / {summary?.total_units ?? 0}
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-[#131B2E] p-5 border border-slate-800 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-mono-tactical">Stok Menipis</p>
            <p className="font-mono-tactical text-2xl font-bold text-amber-400" data-testid="stat-low-stock">
              {summary?.low_stock_count ?? 0} item
            </p>
          </div>
        </div>
        <div className="rounded-xl bg-[#131B2E] p-5 border border-slate-800 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-widest font-mono-tactical">Omset 30 Hari</p>
            <p className="font-mono-tactical text-2xl font-bold text-cyan-400" data-testid="stat-omset-month">
              {formatIDR(summary?.month.total || 0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
